/**
 * Derivative URL rules, client side.
 *
 * Pure functions with no React and no imports, so they can be exercised
 * directly. The server-side counterpart is
 * App\Services\Images\DerivativeGenerator -- the two must produce byte-for-byte
 * identical URLs or the same image is generated twice under two names. They
 * are duplicated rather than shared because a browser cannot read Laravel
 * config; tests/Unit/ImageUrlParityTest.php checks they still agree.
 */

/**
 * Widths we are willing to generate and keep on disk.
 * Must match 'widths' in config/images.php.
 */
export const CANONICAL_WIDTHS = [140, 400, 600, 1600]

/**
 * Masters we can resize, keyed by the token the derivative URL carries.
 * Must match 'sources' in config/images.php.
 */
export const SOURCE_HOSTS = {
    'cdn.coolframes.com': 'cdn',
    'bucket.coolframes.com': 'bucket',
    'www.coolframes.com': 'www',
}

/**
 * Smallest configured width at or above the one asked for.
 *
 * Snapping up rather than to the nearest keeps a slot from being handed an
 * image narrower than it paints, which would be visibly soft.
 */
export const snapWidth = (width) =>
    CANONICAL_WIDTHS.find(w => width <= w) ?? CANONICAL_WIDTHS[CANONICAL_WIDTHS.length - 1]

/**
 * Match PHP's repeated rawurldecode safety check without interpreting path
 * separators. This is only validation; the server remains responsible for
 * canonicalizing the upstream master URL.
 */
const hasSafePathSegments = (path) => path.split('/').every((segment) => {
    let decoded = segment

    for (let attempt = 0; attempt < 8; attempt++) {
        const next = decoded.replace(/%([0-9a-f]{2})/gi, (_match, hex) =>
            String.fromCharCode(Number.parseInt(hex, 16))
        )

        if (next === decoded) {
            break
        }

        decoded = next
    }

    return decoded !== ''
        && decoded !== '.'
        && decoded !== '..'
        && decoded.indexOf('\0') < 0
        && decoded.indexOf('/') < 0
        && decoded.indexOf('\\') < 0
})

/**
 * Split a master URL into the source token and the path beneath it.
 *
 * Returns null for anything the resizer will not fetch -- an unknown host, a
 * non-image, or a URL carrying a query string. The query case matters: a
 * derivative is keyed by path alone, so honouring a cache-busting ?v= would
 * pin a stale file forever. The handful of local marketing images that use
 * one are better left unoptimised than served wrong.
 */
export const resolveSource = (url) => {
    let host
    let path

    if (url.indexOf('//') === 0 || url.indexOf('://') >= 0) {
        const match = url.match(/^(?:https?:)?\/\/([^/]+)\/(.*)$/)

        if (! match) {
            return null
        }

        host = match[1].toLowerCase()
        path = match[2]
    } else {
        // Relative, so this site serves it.
        host = 'www.coolframes.com'
        path = url.replace(/^\/+/, '')
    }

    const source = SOURCE_HOSTS[host]

    if (! source || ! path) {
        return null
    }

    if (path.indexOf('?') >= 0 || path.indexOf('#') >= 0 || ! hasSafePathSegments(path)) {
        return null
    }

    if (! /\.(jpe?g|png|webp)$/i.test(path)) {
        return null
    }

    return { source, path }
}

/**
 * Storyblok serves its own transforms free of charge, so CMS imagery never
 * touches our disk or our CPU. Exact widths are fine here -- unlike our own
 * resizer, another variant costs us nothing.
 */
export const storyblokUrl = (url, width) => {
    if (url.indexOf('/m/') >= 0) {
        return url
    }

    return `${url}/m/${width}x0/filters:quality(82):format(webp)`
}

/**
 * Rewrite a master URL to its derivative, or return it unchanged when there
 * is nothing safe to do with it.
 */
export const derivativeUrl = (url, width, resizeBase) => {
    if (! url) {
        return url
    }

    if (url.indexOf('storyblok.com') >= 0) {
        return storyblokUrl(url, width)
    }

    if (! resizeBase) {
        return url
    }

    // Already a derivative -- do not wrap it a second time.
    if (url.indexOf('/img/r/') >= 0) {
        return url
    }

    const target = resolveSource(url)

    if (! target) {
        return url
    }

    return `${resizeBase.replace(/\/+$/, '')}/${snapWidth(width)}/${target.source}/${target.path}.webp`
}
